import { useEffect, useRef, useState, type CSSProperties } from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import styles from "./Dashboard.module.css";
import { Icon } from "../components/Icon";
import { useCurrentUser, getCurrentUserId } from "../useCurrentUser";
import {
  MAX_COLS,
  STANDARD_LAYOUT,
  WIDGET_KATALOG,
  WIDGET_TYPER,
  lagreLayout,
  lastLayout,
  nyWidgetId,
  type DashboardLayout,
  type WidgetConfig,
  type WidgetInstance,
  type WidgetType,
} from "../lib/dashboard";
import { NyesteSalg } from "../components/widgets/NyesteSalg";
import { MineProsjekter } from "../components/widgets/MineProsjekter";
import { MineOppgaver } from "../components/widgets/MineOppgaver";
import { SisteKalkyler } from "../components/widgets/SisteKalkyler";
import { ProsjektKpiWidget } from "../components/widgets/ProsjektKpi";

function WidgetInnhold({
  w,
  onConfig,
}: {
  w: WidgetInstance;
  onConfig: (id: string, config: WidgetConfig) => void;
}) {
  switch (w.type) {
    case "nyesteSalg":
      return <NyesteSalg />;
    case "mineProsjekter":
      return <MineProsjekter />;
    case "mineOppgaver":
      return <MineOppgaver />;
    case "sisteKalkyler":
      return <SisteKalkyler />;
    case "prosjektKpi":
      return (
        <ProsjektKpiWidget
          projektId={w.config?.projektId}
          onVelg={(pid) => onConfig(w.id, { projektId: pid })}
        />
      );
  }
}

/** Kort-hode med ikon-chip + (i rediger-modus) verktøy. */
function WidgetHode({
  w,
  rediger,
  onFjern,
  onBredde,
  dragProps,
}: {
  w: WidgetInstance;
  rediger?: boolean;
  onFjern?: (id: string) => void;
  onBredde?: (id: string) => void;
  dragProps?: React.HTMLAttributes<HTMLElement>;
}) {
  return (
    <header className={styles.widgetHode}>
      <span className={styles.widgetIkon} aria-hidden="true">
        <Icon name={WIDGET_KATALOG[w.type].ikon} size={15} />
      </span>
      <span className={styles.widgetTittel}>{WIDGET_KATALOG[w.type].navn}</span>
      {rediger && (
        <div className={styles.widgetVerktoy}>
          <button
            type="button"
            className={styles.miniKnapp}
            onClick={() => onBredde?.(w.id)}
            title="Bytt mellom halv og full bredde"
          >
            {w.cols >= MAX_COLS ? "Full" : "Halv"}
          </button>
          <button
            type="button"
            className={`${styles.miniKnapp} ${styles.miniKnappFare}`}
            onClick={() => onFjern?.(w.id)}
            title="Fjern widget"
            aria-label="Fjern widget"
          >
            ×
          </button>
          <span className={styles.dragHandle} title="Dra for å flytte" {...dragProps}>
            <Icon name="grip" size={16} />
          </span>
        </div>
      )}
    </header>
  );
}

function SortableWidget({
  w,
  rediger,
  onFjern,
  onBredde,
  onConfig,
}: {
  w: WidgetInstance;
  rediger: boolean;
  onFjern: (id: string) => void;
  onBredde: (id: string) => void;
  onConfig: (id: string, config: WidgetConfig) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: w.id,
    animateLayoutChanges: () => false,
  });
  // Ingen transform på naboene – de står stille; CSS-grid håndterer ny plassering
  // ved slipp. Kortet som dras vises som svak plassholder.
  const style: CSSProperties = {
    gridColumn: `span ${w.cols}`,
    opacity: isDragging ? 0.35 : 1,
  };
  return (
    <section
      ref={setNodeRef}
      style={style}
      className={`${styles.widget} ${rediger ? styles.widgetRediger : ""} ${
        isDragging ? styles.widgetPlassholder : ""
      }`}
    >
      <WidgetHode
        w={w}
        rediger={rediger}
        onFjern={onFjern}
        onBredde={onBredde}
        dragProps={{ ...attributes, ...listeners }}
      />
      <div className={styles.widgetKropp}>
        <WidgetInnhold w={w} onConfig={onConfig} />
      </div>
    </section>
  );
}

/** «God morgen/dag/kveld, Erik 👋» og dagens dato (var Velkommen-kortet). */
function hilsen(navn: string | undefined) {
  const na = new Date();
  const t = na.getHours();
  const hei = t < 10 ? "God morgen" : t < 18 ? "God dag" : "God kveld";
  const fornavn = navn?.split(" ")[0] ?? "";
  const dato = na.toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long" });
  return { tittel: `${hei}${fornavn ? `, ${fornavn}` : ""} 👋`, dato: dato.charAt(0).toUpperCase() + dato.slice(1) };
}

export function Dashboard() {
  const user = useCurrentUser();
  const { tittel, dato } = hilsen(user?.displayName);
  const uid = user?.userId ?? getCurrentUserId() ?? "default";
  const [layout, setLayout] = useState<DashboardLayout | null>(null);
  const [rediger, setRediger] = useState(false);
  const [galleri, setGalleri] = useState(false);
  const [aktivId, setAktivId] = useState<string | null>(null);
  const lastetUid = useRef<string | null>(null);
  // Hopp over lagring rett etter innlasting, så et urørt standardoppsett ikke
  // fryses i localStorage (da ville nettleseren aldri fått nye standarder).
  const hoppOverLagring = useRef(false);

  useEffect(() => {
    setLayout(lastLayout(uid));
    lastetUid.current = uid;
    hoppOverLagring.current = true;
  }, [uid]);

  useEffect(() => {
    if (!layout || lastetUid.current !== uid) return;
    if (hoppOverLagring.current) {
      hoppOverLagring.current = false;
      return;
    }
    lagreLayout(uid, layout);
  }, [layout, uid]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const onDragStart = (e: DragStartEvent) => setAktivId(String(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    setAktivId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setLayout((l) => {
      if (!l) return l;
      const fra = l.widgets.findIndex((w) => w.id === active.id);
      const til = l.widgets.findIndex((w) => w.id === over.id);
      if (fra < 0 || til < 0) return l;
      return { ...l, widgets: arrayMove(l.widgets, fra, til) };
    });
  };

  const fjern = (id: string) =>
    setLayout((l) => (l ? { ...l, widgets: l.widgets.filter((w) => w.id !== id) } : l));
  const bredde = (id: string) =>
    setLayout((l) =>
      l
        ? {
            ...l,
            widgets: l.widgets.map((w) =>
              w.id === id ? { ...w, cols: (w.cols % MAX_COLS) + 1 } : w
            ),
          }
        : l
    );
  const leggTil = (type: WidgetType) =>
    setLayout((l) =>
      l
        ? {
            ...l,
            widgets: [
              ...l.widgets,
              { id: nyWidgetId(type), type, cols: WIDGET_KATALOG[type].standardCols },
            ],
          }
        : l
    );
  const settConfig = (id: string, config: WidgetConfig) =>
    setLayout((l) =>
      l
        ? {
            ...l,
            widgets: l.widgets.map((w) =>
              w.id === id ? { ...w, config: { ...w.config, ...config } } : w
            ),
          }
        : l
    );
  const tilbakestill = () =>
    setLayout({
      versjon: STANDARD_LAYOUT.versjon,
      widgets: STANDARD_LAYOUT.widgets.map((w) => ({ ...w })),
    });

  if (!layout) {
    return (
      <div className={styles.side}>
        <p className={styles.undertittel}>Laster dashbord…</p>
      </div>
    );
  }

  const aktivWidget = aktivId ? layout.widgets.find((w) => w.id === aktivId) : null;

  return (
    <div className={styles.side}>
      <div className={styles.topp}>
        <div>
          <h1 className={styles.tittel}>{tittel}</h1>
          <p className={styles.undertittel}>
            {rediger ? "Dra i håndtaket for å flytte, endre bredde, eller legg til nye." : dato}
          </p>
        </div>
        <div className={styles.toppKnapper}>
          {rediger && (
            <button type="button" className={styles.knapp} onClick={() => setGalleri((g) => !g)}>
              <Icon name="plus" size={16} /> Legg til widget
            </button>
          )}
          {rediger && (
            <button
              type="button"
              className={`${styles.knapp} ${styles.knappSubtil}`}
              onClick={tilbakestill}
              title="Sett tilbake til standardoppsettet"
            >
              Tilbakestill
            </button>
          )}
          <button
            type="button"
            className={`${styles.knapp} ${rediger ? styles.knappPrimar : ""}`}
            onClick={() => {
              setRediger((r) => !r);
              setGalleri(false);
            }}
          >
            {rediger ? "Ferdig" : "Tilpass"}
          </button>
        </div>
      </div>

      {rediger && galleri && (
        <div className={styles.galleri}>
          <p className={styles.galleriTittel}>Legg til en widget</p>
          <div className={styles.galleriGrid}>
            {WIDGET_TYPER.map((t) => (
              <button
                key={t}
                type="button"
                className={styles.galleriKort}
                onClick={() => leggTil(t)}
              >
                <span className={styles.galleriIkon}>
                  <Icon name={WIDGET_KATALOG[t].ikon} size={18} />
                </span>
                <span className={styles.galleriTekst}>
                  <span className={styles.galleriNavn}>{WIDGET_KATALOG[t].navn}</span>
                  <span className={styles.galleriBesk}>{WIDGET_KATALOG[t].beskrivelse}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setAktivId(null)}
      >
        <SortableContext items={layout.widgets.map((w) => w.id)} strategy={rectSortingStrategy}>
          <div className={`${styles.grid} ${rediger ? styles.gridRediger : ""}`}>
            {layout.widgets.length === 0 && (
              <div className={styles.tom}>
                Ingen widgets ennå. Trykk «Tilpass» → «Legg til widget».
              </div>
            )}
            {layout.widgets.map((w) => (
              <SortableWidget
                key={w.id}
                w={w}
                rediger={rediger}
                onFjern={fjern}
                onBredde={bredde}
                onConfig={settConfig}
              />
            ))}
          </div>
        </SortableContext>

        <DragOverlay>
          {aktivWidget ? (
            <section className={`${styles.widget} ${styles.widgetOverlay}`}>
              <WidgetHode w={aktivWidget} />
              <div className={styles.widgetKropp}>
                <WidgetInnhold w={aktivWidget} onConfig={() => {}} />
              </div>
            </section>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
